import { createFileRoute, Link } from "@tanstack/react-router";
import { BRAND } from "@/lib/behot";
import { LEGAL_OG_IMAGE, LEGAL_OG_IMAGE_ALT, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/privacy")({
  head: () =>
    buildPageMetadata({
      path: "/privacy",
      title: "Privacy Policy | BeHot.Now",
      description:
        "How BeHot.Now collects, uses, stores and protects personal data for accounts, listings, payments, payouts and referrals.",
      ogTitle: "Privacy Policy",
      keywords: [
        "privacy policy",
        "GDPR data rights",
        "personal data handling",
        "data retention",
        "creator platform privacy",
      ],
      ogDescription: "How BeHot.Now handles your personal data.",
      image: LEGAL_OG_IMAGE,
      imageAlt: LEGAL_OG_IMAGE_ALT,
      twitterCard: "summary_large_image",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Privacy", path: "/privacy" },
      ],
    }),
  component: Privacy,
});

const UPDATED = "24 August 2026";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-bold">{title}</h2>
      <div className="mt-2 space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

function P({ text }: { text: string }) {
  return (
    <>
      {text.split("\n\n").map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </>
  );
}

function Privacy() {
  const { t, locale } = useT();
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-4xl font-bold">{t("legal.privacy.title")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("legal.privacy.updated", { date: UPDATED })}</p>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
        {t("legal.privacy.intro", { operator: BRAND.operator, domain: BRAND.domain })}
      </p>

      <Section title={t("legal.privacy.s1.title")}>
        <P text={t("legal.privacy.s1.body", { operator: BRAND.operator })} />
      </Section>

      <Section title={t("legal.privacy.s2.title")}>
        <p>
          <strong>{t("legal.privacy.s2.accountLabel")}</strong>
          {t("legal.privacy.s2.accountBody")}
        </p>
        <p>
          <strong>{t("legal.privacy.s2.listingLabel")}</strong>
          {t("legal.privacy.s2.listingBody")}
        </p>
        <p>
          <strong>{t("legal.privacy.s2.billingLabel")}</strong>
          {t("legal.privacy.s2.billingBody")}
        </p>
        <p>
          <strong>{t("legal.privacy.s2.transactionLabel")}</strong>
          {t("legal.privacy.s2.transactionBody")}
        </p>
        <p>
          <strong>{t("legal.privacy.s2.referralLabel")}</strong>
          {t("legal.privacy.s2.referralBody")}
        </p>
        <p>
          <strong>{t("legal.privacy.s2.technicalLabel")}</strong>
          {t("legal.privacy.s2.technicalBody")}
        </p>
      </Section>

      <Section title={t("legal.privacy.s3.title")}>
        <P text={t("legal.privacy.s3.body")} />
      </Section>

      <Section title={t("legal.privacy.s4.title")}>
        <P text={t("legal.privacy.s4.body")} />
      </Section>

      <Section title={t("legal.privacy.s5.title")}>
        <P text={t("legal.privacy.s5.body")} />
      </Section>

      <Section title={t("legal.privacy.s6.title")}>
        <P text={t("legal.privacy.s6.body")} />
      </Section>

      <Section title={t("legal.privacy.s7.title")}>
        <P text={t("legal.privacy.s7.body")} />
      </Section>

      <Section title={t("legal.privacy.s8.title")}>
        <P text={t("legal.privacy.s8.body")} />
      </Section>

      <Section title={t("legal.privacy.s9.title")}>
        <P text={t("legal.privacy.s9.body")} />
      </Section>

      <Section title={t("legal.privacy.s10.title")}>
        <P text={t("legal.privacy.s10.body")} />
      </Section>

      <Section title={t("legal.privacy.s11.title")}>
        <P text={t("legal.privacy.s11.body")} />
      </Section>

      <p className="mt-10 text-sm text-muted-foreground">
        {t("legal.common.seeAlso")}{" "}
        <Link to="/cookie" className="text-primary hover:underline">
          {t("legal.common.cookiePolicy")}
        </Link>{" "}
        {t("legal.common.and")}{" "}
        <Link to="/terms" className="text-primary hover:underline">
          {t("legal.common.termsOfService")}
        </Link>
        .
      </p>

      {locale !== "en" ? (
        <p className="mt-6 text-xs text-muted-foreground">{t("legal.common.englishPrevails")}</p>
      ) : null}
    </div>
  );
}
